<template>
  <el-dropdown v-if="isOwner" trigger="click" class="flex-shrink-0" :disabled="switching" :placement="placement"
    @visible-change="onVisibleChange" @command="onSwitch">
    <div class="flex items-center gap-1 cursor-pointer hover:opacity-80">
      <slot />
      <Icon icon="mdi:chevron-down" class="text-xs text-[#6D6D6D]" />
    </div>
    <template #dropdown>
      <el-dropdown-menu class="min-w-[240px]">
        <div class="px-4 py-2 text-xs font-semibold text-gray-500">{{ t('switchStore') }}</div>
        <div v-if="loading" class="flex justify-center px-4 py-3">
          <LoadingSpinner />
        </div>
        <div v-else-if="!machines.length" class="px-4 py-3 text-sm text-gray-400">{{ t('noStores') }}</div>
        <template v-else>
          <el-dropdown-item v-for="machine in machines" :key="machine.id" :command="machine"
            :disabled="isCurrent(machine)">
            <div class="flex w-full items-center justify-between gap-4">
              <span class="font-semibold">{{ localizedName(machine.location) }}</span>
              <span class="flex items-center gap-1 text-xs text-gray-500">
                <Icon icon="mdi:cash-register" />
                {{ localizedName(machine.register) || localizedName(machine) }}
                <Icon v-if="isCurrent(machine)" icon="mdi:check" class="text-green-600" />
              </span>
            </div>
          </el-dropdown-item>
        </template>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
  <div v-else class="flex-shrink-0">
    <slot />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Icon } from '@iconify/vue'
import { ElMessage } from 'element-plus'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { useAuth } from '@/plugins/auth'
import { useContext } from '@/plugins/context'
import { Machine } from '@/models/company/Machine'

export default defineComponent({
  name: 'StoreSwitcher',
  components: { Icon, LoadingSpinner },

  setup() {
    const { t, locale } = useI18n({
      messages: {
        en: {
          switchStore: 'Switch store',
          noStores: 'No other stores available',
          switchingTo: 'Switching to',
          switchFailed: 'Failed to switch store'
        },
        ar: {
          switchStore: 'التبديل بين المتاجر',
          noStores: 'لا توجد متاجر أخرى متاحة',
          switchingTo: 'جاري التبديل إلى',
          switchFailed: 'فشل التبديل بين المتاجر'
        }
      }
    })
    const ctx = useContext()
    const { getSwitchableMachines, switchStore } = useAuth()

    const machines = ref<Machine[]>([])
    const loading = ref(false)
    const switching = ref(false)

    const isOwner = computed(() => !!ctx.currentUser?.isOwner)
    const placement = computed(() => (locale.value === 'ar' ? 'bottom-start' : 'bottom-end'))

    const localizedName = (o?: { enName?: string; arName?: string }) =>
      (locale.value === 'ar' ? o?.arName || o?.enName : o?.enName || o?.arName) || ''

    const isCurrent = (machine: Machine) => machine.id === ctx.currentMachine?.id

    const onVisibleChange = async (visible: boolean) => {
      if (!visible || loading.value) return
      loading.value = true
      try {
        machines.value = await getSwitchableMachines()
      } catch (e: any) {
        ElMessage.error(e?.title || t('switchFailed'))
      } finally {
        loading.value = false
      }
    }

    const onSwitch = async (machine: Machine) => {
      if (!machine.id || isCurrent(machine) || switching.value) return
      switching.value = true
      try {
        await switchStore(machine.id)
        ElMessage.success(`${t('switchingTo')} ${localizedName(machine.location)} - ${localizedName(machine.register)}...`)
        window.location.reload()
      } catch (e: any) {
        ElMessage.error(e?.title || t('switchFailed'))
        switching.value = false
      }
    }

    return { t, isOwner, placement, machines, loading, switching, localizedName, isCurrent, onVisibleChange, onSwitch }
  }
})
</script>
